// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what may travel on the wire, and what is refused at the door
//  ---------------------------------------------------------------------------
//  The room relays and cannot read (server/room.js). That is the property the
//  whole design rests on, and its price is that everything arriving is
//  untrusted: an old version, a truncated body, a damaged frame, a stranger.
//  This file is the first thing that happens to any of it, and it is pure —
//  no socket, no storage, no clock — so every rule below is provable without
//  either end of a connection.
//
//  IT REFUSES RATHER THAN GUESSES, the same stance md-crypto.ts takes on an
//  envelope it cannot open. A version this build does not know, a type with no
//  meaning, a frame with nobody's name on it: `null`. Handing back a
//  half-understood frame "in case it was usable" is how a document nobody wrote
//  reaches somebody's day.
//
//  THE ENCODING IS UTF-8 JSON, and that is a decision rather than a default.
//  The CRDT state is already a base64 string (md-crdt.ts:76), so a `doc` frame
//  carries the VERY VALUE sync carries — same key, same shape — and the
//  receiving side therefore needs no new applier. The live path is the pull
//  path, which is the single property that makes it safe. The 33% base64 costs
//  bytes on a socket and saves an entire second implementation of the merge.
//
//  The transport underneath stays opaque: IT never decodes. This does, one
//  layer up, which is exactly the seam md-transport.ts describes.
//
//  IT ALSO DECIDES WHAT TIER 1 CARRIES. That is policy, not encoding, and it is
//  here so there is one place to read it. It began as THE DAY and nothing else,
//  and 3.1 widened it to the desk, the desk's pictures, where a record stands
//  and a database row — the things a second person can watch move. Everything
//  else is still the nudge. If the door took any key the applier would happily
//  apply it and the live surface would quietly become the whole account: a
//  decision nobody took, arrived at by omission.
//
//  WHY ALL FOUR DAY DOCUMENTS AND NOT JUST THE RECORDS. `tombstones:<day>` has
//  to travel or the clock-only fallback resurrects a deleted record — with no
//  Yjs state, mdMergeRecords cannot tell "deleted there" from "absent there",
//  and absence loses to a local copy. `completed:<day>` travels because ticking
//  something off is the most visible thing a second device can do, and a
//  grow-only set is the safest thing there is to merge.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MD_LIVE_VERSION = 1;

  /* Mirrors MAX_VALUE_BYTES in server/sync.js. A frame the store would refuse
     must not travel further than the store will take it, or the wire becomes a
     way to hand another device something it can never persist.
     tests/live-frame.test.js reads that file so the two cannot drift.

     The honest cost: a day at the very cap does not travel live. It still
     travels — by HTTP, on the ordinary schedule — which is the whole floor this
     phase stands on. */
  const MD_LIVE_MAX_BYTES = 1024 * 1024;

  /* `evict` is bare, like `hello`, `beat` and `bye` — it says nothing beyond
     "this account was deleted from another device", so v/t/dev is the whole
     frame and no `key`/`value` branch below has to know it exists.

     `signout` is its neighbour and the same shape: not "this account is gone"
     but "the sessions on it are". A device that hears it knows the only thing it
     needs to, which is that its own key has stopped working. */
  const TYPES = ['doc', 'news', 'hello', 'beat', 'bye', 'evict', 'signout'];

  /* What tier 1 carries. `crdt:` rides on the two record kinds and on the
     database row and on none of the others, exactly as server/sync.js's
     KEY_PATTERN has it — `completed` is grow-only, `tombstones` is already a
     merge structure, and a desk scene converges by element on the engine's own
     stamps, so none of the three has a Yjs state.

     THE DAY WAS THE WHOLE LIST UNTIL 3.1, and the reason it was is worth
     keeping: a desk document was a THREE-MEGABYTE DATA URL, and putting one on
     a socket per stroke would have been a worse app, not a faster one. 1.7 and
     1.8 removed that — a picture is now a name, and a scene past the document
     cap spills to its own bytes and leaves `{spill, elements, at}` behind — so
     every document listed here is small by construction. The four families
     added are the ones a second person can actually watch move: the desk and
     its pictures, where a record stands, and a row in a shared library.

     STILL NOT HERE, deliberately: `boards`, `projects`, `project-records`,
     `workflows` and the rest. They are lists an edit rewrites whole, so live
     traffic would be the whole list on every keystroke for a fact the nudge
     already carries in one frame. And `record-index` is derived, never pushed
     at all (md-sync.js says so twice).

     THREE FILES HAVE TO AGREE ABOUT THIS LINE: this one sends, server/sync.js's
     KEY_PATTERN stores, and md-sync.js's mdSanitiseDoc applies. A family that
     has only two of the three ships INERT and silent — the frame encodes, the
     room relays it, and the far side drops it without a word. The decision
     itself now lives in app/md-families.ts — one `live` column per family —
     and tests/live-carries-in-step.test.js reads it against all three.

     A SCENE STAYS ON THE WIRE, decided on purpose in 5.x and recorded here
     because the revamp's plan recommended the opposite. The case for dropping
     `board:` was that a nudge already compensates and one mechanism is better
     than two. Three things answer it:

       The size argument it rested on was retired by 1.7 and 1.8, above. A desk
       document is a few dozen characters plus small elements now, or a NAME
       when it is not — the three-megabyte data URL that made this unthinkable
       does not exist any more.

       It is not a second mechanism. A live `doc` frame goes through the same
       sanitiser and the same applier as a pull; that is the property that made
       tier 1 safe in the first place and it is why md-board.ts's spill,
       fault-hold and merge branches all apply to a frame without knowing one
       arrived. Removing it would remove a path, not a mechanism.

       And it costs a person something they can see: a desk that keeps up with
       the other device as the pen moves, rather than one HTTP round trip later.
       The nudge is the right answer for a list an edit rewrites whole. It is
       the wrong one for the surface somebody is watching. */
  const CARRIES = /^(?:crdt:)?(?:events|records):\d{4}-\d{2}-\d{2}$|^(?:completed|tombstones):\d{4}-\d{2}-\d{2}$|^board:[a-z0-9]{6,40}$|^boardfile:[a-z0-9]{6,64}$|^record-status$|^(?:crdt:)?database-row:[a-z0-9]{6,40}:[a-z0-9]{6,40}$/;

  /** Is this a document tier 1 puts on the wire? Names the sync document
      namespace (`md:` already stripped), which is what a frame carries. */
  function mdLiveCarries(key: unknown): boolean {
    return typeof key === 'string' && CARRIES.test(key);
  }

  /* The canonical frame, or null. Unknown fields are DROPPED rather than
     refused: a later build may add one, and this build must not hand an applier
     a field it would then have to guess the meaning of. Refusing them outright
     would make every addition a breaking change; passing them on would make
     every addition this build's problem. */
  function normalise(raw: any): any {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (raw.v !== MD_LIVE_VERSION) return null;

    const t = raw.t;
    if (typeof t !== 'string' || TYPES.indexOf(t) < 0) return null;

    /* Every frame says which device sent it. Without that a device cannot tell
       its own voice from company, and "your other device is here" would light up
       for one device on its own. */
    const dev = raw.dev;
    if (typeof dev !== 'string' || !dev) return null;

    if (t !== 'doc') {
      const out: any = { v: MD_LIVE_VERSION, t, dev };
      /* Account-wide presence stays cheap, but a visible desk change is useful
         enough to travel immediately. Old clients drop this field; new clients
         treat null as online elsewhere. */
      if ((t === 'hello' || t === 'beat') && 'desk' in raw) {
        if (raw.desk !== null && (typeof raw.desk !== 'string' || !/^[a-z0-9]{10}$/.test(raw.desk))) return null;
        out.desk = raw.desk;
      }
      return out;
    }

    if (!mdLiveCarries(raw.key)) return null;
    /* `value: null` is a deletion and travels. A MISSING value is a frame that
       never said what it was for, and confusing the two is how "I have not got
       this one" becomes "this one is empty" — the loss sync-nothing-lost.html
       exists about. */
    if (!('value' in raw)) return null;

    return { v: MD_LIVE_VERSION, t, dev, key: raw.key, value: raw.value };
  }

  /** A frame as bytes, or null if it is not a frame this build would send. */
  function mdLiveEncode(frame: any): Uint8Array | null {
    const ok = normalise(frame);
    if (!ok) return null;
    if (typeof g.TextEncoder !== 'function') return null;

    let text = '';
    try {
      text = JSON.stringify(ok);
    } catch {
      /* A value that cannot be serialised — a cycle, a BigInt. Nothing to send,
         and nothing to throw at a caller who is mid-keystroke. */
      return null;
    }

    const bytes = new g.TextEncoder().encode(text);
    if (!bytes.byteLength || bytes.byteLength > MD_LIVE_MAX_BYTES) return null;
    return bytes;
  }

  /** Bytes as a frame, or null. Every refusal is silent on purpose: a frame this
      device cannot read is not an error a person can act on. */
  function mdLiveDecode(bytes: any): any {
    if (!(bytes instanceof Uint8Array)) return null;
    /* Length first, so an oversized body costs a comparison rather than a parse
       of however much somebody chose to send. */
    if (!bytes.byteLength || bytes.byteLength > MD_LIVE_MAX_BYTES) return null;
    if (typeof g.TextDecoder !== 'function') return null;

    let text = '';
    try {
      /* fatal, so bytes that are not UTF-8 are refused rather than silently
         becoming U+FFFD. A frame that decoded to replacement characters would
         parse, apply, and quietly put mojibake in somebody's writing. */
      text = new g.TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return null;
    }

    let raw: any = null;
    try {
      raw = JSON.parse(text);
    } catch {
      return null;
    }
    return normalise(raw);
  }

  Object.assign(g, {
    MD_LIVE_VERSION, MD_LIVE_MAX_BYTES,
    mdLiveCarries, mdLiveEncode, mdLiveDecode,
  });
})(typeof window !== 'undefined' ? window : globalThis);
